import { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  useDisclosure,
  useToast,
  Box,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { AddIcon, SearchIcon, RepeatIcon } from '@chakra-ui/icons';

import Card from 'components/card/Card';
import ContactSearchBar from 'components/acturis/ContactSearchBar';
import ContactResultsTable from 'components/acturis/ContactResultsTable';
import AddCustomerModal from './components/AddCustomerModal';
import ActurisConfigBlocker from './components/ActurisConfigBlocker';

import { useUrlFilters } from 'hooks/useUrlFilters';
import {
  useActurisConfigStatus,
  useContactSearch,
  useContactOperation,
  useRecentContacts,
} from './hooks';
import { AuthContext } from 'contexts/AuthContext';
import { ROLE_ADMIN } from 'variables/constants';

import type { CustomerFormData } from './components/CustomerForm';
import type { ContactSearchType, ActurisContact } from 'api/quote-marketplace';

const FILTER_KEYS = ['search_type', 'search'] as const;

const TABLE_HEADERS = ['Name', 'Address', 'Postal Code'];

const PAGE_SIZE = 10;

const QuoteMarketplaceView = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { setFilters, resetAll, searchParams } = useUrlFilters(FILTER_KEYS);

  const { user } = useContext(AuthContext);

  const {
    syncStatus,
    syncError,
    canAccess,
    missingFields,
    message,
    isLoading: isConfigLoading,
    resync,
  } = useActurisConfigStatus();
  const isAdmin = user?.role === ROLE_ADMIN;

  const [searchType, setSearchType] = useState<ContactSearchType>('name');
  const [searchValue, setSearchValue] = useState('');

  const {
    results,
    count,
    status: searchStatus,
    error: searchError,
    isSearching,
    triggerSearch,
    clearSearch,
    activeSearch,
  } = useContactSearch({ enabled: syncStatus === 'ready' });

  const { isPending: isCreatingContact, createContact } = useContactOperation();

  const {
    contacts: recentContacts,
    status: recentStatus,
    isLoading: isLoadingRecent,
  } = useRecentContacts({
    enabled: syncStatus === 'ready',
  });

  const lastTriggeredRef = useRef<string | null>(null);

  useEffect(() => {
    const urlSearchType =
      (searchParams.get('search_type') as ContactSearchType) || 'name';
    const urlSearch = searchParams.get('search') || '';
    setSearchType(urlSearchType);
    setSearchValue(urlSearch);

    if (!urlSearch) {
      lastTriggeredRef.current = null;
      return;
    }

    if (syncStatus !== 'ready') return;

    const searchKey = `${urlSearchType}|${urlSearch}`;
    if (lastTriggeredRef.current === searchKey) return;

    lastTriggeredRef.current = searchKey;
    triggerSearch(urlSearch, urlSearchType, 1, PAGE_SIZE);
  }, [searchParams, syncStatus]);

  const hasActiveSearch = activeSearch.trim().length > 0;

  const handleRowClick = (contact: ActurisContact) => {
    navigate(`/admin/quote-marketplace/customer/${contact.contact_ref}`);
  };

  const handleCreateCustomer = async (data: CustomerFormData) => {
    try {
      const contactRef = await createContact(data);
      onClose();
      toast({
        title: 'Customer created',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      navigate(`/admin/quote-marketplace/customer/${contactRef}`);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create customer';
      toast({
        title: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const handleSearch = () => {
    const trimmedSearch = searchValue.trim();
    if (!trimmedSearch) return;

    lastTriggeredRef.current = `${searchType}|${trimmedSearch}`;
    triggerSearch(trimmedSearch, searchType, 1, PAGE_SIZE);
    setFilters({
      search_type: searchType,
      search: trimmedSearch,
    });
  };

  const handleReset = () => {
    lastTriggeredRef.current = null;
    setSearchType('name');
    setSearchValue('');
    clearSearch();
    resetAll();
  };

  const isSyncing = !isConfigLoading && syncStatus === 'syncing';

  if (!isConfigLoading && syncStatus === 'error') {
    return (
      <Card
        flexDirection="column"
        w="100%"
        px="0px"
        overflowX={{ sm: 'scroll', lg: 'hidden' }}
      >
        <Box p="20px">
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>Acturis Sync Failed</AlertTitle>
              <AlertDescription>
                {syncError || 'Failed to sync your Acturis configuration.'}
              </AlertDescription>
            </Box>
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              leftIcon={<RepeatIcon />}
              onClick={() => resync()}
            >
              Retry
            </Button>
          </Alert>
        </Box>
      </Card>
    );
  }

  if (!isConfigLoading && !isSyncing && !canAccess) {
    return (
      <Card
        flexDirection="column"
        w="100%"
        px="0px"
        overflowX={{ sm: 'scroll', lg: 'hidden' }}
      >
        <Box p="20px">
          <ActurisConfigBlocker
            missingFields={missingFields}
            message={message}
            isAdmin={isAdmin}
            userId={user?.id}
          />
        </Box>
      </Card>
    );
  }

  if (searchStatus === 'error' && searchError) {
    return (
      <Card
        flexDirection="column"
        w="100%"
        px="0px"
        overflowX={{ sm: 'scroll', lg: 'hidden' }}
      >
        <Box p="20px">
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Search Error</AlertTitle>
            <AlertDescription>{searchError}</AlertDescription>
          </Alert>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      {/* Search Section */}
      <Box px="20px">
        <ContactSearchBar
          searchType={searchType}
          onSearchTypeChange={setSearchType}
          searchValue={searchValue}
          onSearchValueChange={setSearchValue}
          onSearch={handleSearch}
          onReset={handleReset}
          hasActiveSearch={hasActiveSearch}
          isDisabled={isSyncing}
          actions={
            <Button
              leftIcon={<AddIcon />}
              colorScheme="yellow"
              size="md"
              onClick={onOpen}
              isDisabled={isSyncing}
            >
              Add New
            </Button>
          }
        />
      </Box>

      {/* Syncing State */}
      {!hasActiveSearch && !isSearching && isSyncing && (
        <Box textAlign="center" mt="20px">
          <Spinner size="md" />
          <Text mt={2}>Syncing your Acturis configuration...</Text>
        </Box>
      )}

      {!hasActiveSearch && !isSearching && !isSyncing && isLoadingRecent && (
        <Box textAlign="center" mt="20px">
          <Spinner size="md" />
          <Text mt={2}>Loading contacts...</Text>
        </Box>
      )}

      {/* Contacts Table (Recent) */}
      {!hasActiveSearch &&
        !isSearching &&
        !isSyncing &&
        !isLoadingRecent &&
        recentStatus === 'complete' && (
          <>
            <ContactResultsTable
              headers={TABLE_HEADERS}
              contacts={recentContacts}
              onRowClick={handleRowClick}
            />
            {recentContacts.length === 0 && (
              <Box textAlign="center" mt="40px" mb="40px" px="20px">
                <SearchIcon boxSize={8} color="gray.400" mb={4} />
                <Text fontSize="lg" fontWeight="500" color="gray.600">
                  No contacts found
                </Text>
                <Text fontSize="sm" color="gray.500" mt={2}>
                  Search for a contact or create a new one
                </Text>
              </Box>
            )}
          </>
        )}

      {!hasActiveSearch &&
        !isSearching &&
        !isSyncing &&
        !isLoadingRecent &&
        recentStatus !== 'complete' && (
          <Box textAlign="center" mt="20px">
            <Spinner size="md" />
            <Text mt={2}>Loading contacts...</Text>
          </Box>
        )}

      {/* Loading State */}
      {isSearching && (
        <Box textAlign="center" mt="20px">
          <Spinner size="md" />
          <Text mt={2}>Loading contacts...</Text>
        </Box>
      )}

      {/* Contacts Table (Search) */}
      {!isSearching && searchStatus === 'complete' && hasActiveSearch && (
        <>
          <Text
            fontSize="sm"
            fontWeight="600"
            color="gray.500"
            px="20px"
            mt="20px"
          >
            Contacts ({count})
          </Text>
          <ContactResultsTable
            headers={TABLE_HEADERS}
            contacts={results}
            onRowClick={handleRowClick}
          />

          {results.length === 0 && (
            <Box textAlign="center" w="100%" mt="20px" mb="0px">
              No contacts found
            </Box>
          )}
        </>
      )}

      <AddCustomerModal
        isOpen={isOpen}
        onClose={onClose}
        onSave={handleCreateCustomer}
        isSaving={isCreatingContact}
      />
    </Card>
  );
};

export default QuoteMarketplaceView;
